import React, { useState } from "react";

import { terpData } from "@/constants/strains";
import { useStrainPageContext } from "@/context/StrainPageContext";
import { Terp } from "@/custom-types/Strain";
import { TerpAttribute } from "@/custom-types/StrainV2";
import useOnComponentInView from "@/hooks/useOnComponentInView";
import { moveObjToSortedArray } from "@/utils/moveObjToSortedArray";

import StrainTile from "@/components/botanic/StrainTile";
import TerpeneIcon from "@/components/Icons/terpene.svg";
import Section from "@/components/StrainPage/Section";
import TooltipCTA from "@/components/StrainPage/TooltipCTA";

const StrainScience = () => {
  const {
    strain: { terps, name, category, symbol },
  } = useStrainPageContext();

  const [loading, setLoading] = useState(true);

  const [ref] = useOnComponentInView(() => setLoading(false), {
    freezeOnceVisible: true,
  });

  const showSection = Object.values(terps).some((terp) => terp?.score > 0);

  if (!showSection) {
    return null;
  }

  const sortedTerps: TerpAttribute[] = moveObjToSortedArray(
    terps,
    "score",
  ).filter((terp) => terp.score > 0);
  const topTerp = sortedTerps[0];
  const otherTerps = sortedTerps.length >= 2 ? sortedTerps.slice(1, 3) : [];
  const topTerpName = topTerp.name;

  return (
    <div id="strain-terpenes-section" ref={ref}>
      {!loading && sortedTerps.length ? (
        <Section title={`${name} strain science`} className="container">
          <TooltipCTA
            title="Certified Strain Science"
            tooltipText="Terpenes not only determine the flavor and aroma of a cannabis strain, they may also modify its effects. We’ve sourced this terpene data from products tested in Leafly Certified labs."
            sectionName="terpenes"
          />

          <div className="row mt-2">
            <div className="md:col-1/2 lg:col-1/3 mb-xl md:mb-none flex flex-row items-start gap-[16px]">
              <StrainTile
                name={name}
                symbol={symbol}
                category={category}
                className="flex-shrink-0"
              />

              <div>
                <div className="font-bold mb-xs">Phenotype</div>
                <div className="flex items-center mb-sm">
                  <div
                    className={`bg-strain-${category.toLowerCase()} w-[13px] h-[16px] rounded-[2px] mr-sm flex-shrink-0`}
                  ></div>
                  <div>
                    <div>
                      <span>{category}</span>
                    </div>
                  </div>
                </div>

                <div className="font-bold mb-xs">Dominant terpene</div>
                <div className="flex items-stretch mb-sm">
                  <div
                    className={`bg-${topTerp.name.toLowerCase()} w-[8px] rounded mr-sm flex-shrink-0`}
                  ></div>
                  <div>
                    <div>
                      <span>{topTerp.name}</span>
                      <span className="text-grey">{` (${terpData[topTerpName].flavor})`}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-1/2 lg:col-2/3">
              <h3>{name} terpenes</h3>

              <div className="mb-md text-xs">
                The most abundant terpene in {name} is
                <TerpInfo topTerp={topTerp} otherTerps={otherTerps} />
              </div>

              <div className="flex relative mb-sm">
                <div className="mr-sm" style={{ width: 8 }}>
                  <TerpeneIcon
                    className={`text-${topTerp.name.toLowerCase()} absolute`}
                    height="100%"
                    width="8"
                  />
                </div>
                <div>
                  <div>
                    <span className="font-bold">{`${topTerp.name}`}</span>
                    <span className="text-grey">{` (${terpData[topTerpName].flavor})`}</span>
                  </div>
                  <div className="text-xs">{topTerp.description}</div>
                </div>
              </div>

              {otherTerps.map((terp) => (
                <div className="flex relative mb-sm" key={terp.name}>
                  <div className="mr-sm" style={{ width: 8 }}>
                    <TerpeneIcon
                      className={`text-${terp.name.toLowerCase()} absolute`}
                      height="100%"
                      width="8"
                    />
                  </div>
                  <div>
                    <span className="font-bold">{terp.name}</span>
                    <span className="text-grey">{` (${
                      terpData[terp.name].flavor
                    })`}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      ) : null}
    </div>
  );
};

type TerpProps = {
  topTerp: Terp;
  otherTerps: Terp[];
};

const TerpInfo = ({ topTerp, otherTerps }: TerpProps) => {
  if (topTerp && otherTerps.length === 2) {
    return (
      <span>
        <strong> {topTerp?.name.toLowerCase()}</strong>, followed by{" "}
        <strong>{otherTerps[0]?.name.toLowerCase()}</strong> and{" "}
        <strong>{otherTerps[1]?.name.toLowerCase()}</strong>.{" "}
      </span>
    );
  } else if (topTerp && otherTerps.length === 1) {
    return (
      <span>
        <strong>{topTerp?.name.toLowerCase()}</strong>, followed by{" "}
        <strong>{otherTerps[0]?.name.toLowerCase()}</strong>.{" "}
      </span>
    );
  } else {
    return (
      <span>
        <strong>{topTerp?.name.toLowerCase()}</strong>.{" "}
      </span>
    );
  }
};

export default StrainScience;
